import { useMemo } from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  getStraightPath,
  useNodes,
  type EdgeProps,
  type Node,
} from "@xyflow/react";

type Point = { x: number; y: number };
type Rect = { x: number; y: number; w: number; h: number };

const PAD = 10;
const EPS = 0.5;

function nodeRect(n: Node): Rect | null {
  const w = n.measured?.width ?? (n.width as number | undefined) ?? 160;
  const h = n.measured?.height ?? (n.height as number | undefined) ?? 80;
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return { x: n.position.x, y: n.position.y, w, h };
}

function inflate(r: Rect, pad: number): Rect {
  return { x: r.x - pad, y: r.y - pad, w: r.w + pad * 2, h: r.h + pad * 2 };
}

function corners(r: Rect): Point[] {
  return [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x, y: r.y + r.h },
    { x: r.x + r.w, y: r.y + r.h },
  ];
}

function dist(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function pointInRect(p: Point, r: Rect): boolean {
  return p.x > r.x + EPS && p.x < r.x + r.w - EPS && p.y > r.y + EPS && p.y < r.y + r.h - EPS;
}

/** Proper segment–AABB intersection (interior), corners on boundary OK. */
function segmentHitsRect(a: Point, b: Point, r: Rect): boolean {
  if (pointInRect(a, r) || pointInRect(b, r)) return true;

  const left = r.x;
  const right = r.x + r.w;
  const top = r.y;
  const bottom = r.y + r.h;

  const edges: [Point, Point][] = [
    [
      { x: left, y: top },
      { x: right, y: top },
    ],
    [
      { x: right, y: top },
      { x: right, y: bottom },
    ],
    [
      { x: right, y: bottom },
      { x: left, y: bottom },
    ],
    [
      { x: left, y: bottom },
      { x: left, y: top },
    ],
  ];

  for (const [c, d] of edges) {
    if (segmentsCross(a, b, c, d)) return true;
  }
  return false;
}

function segmentsCross(a: Point, b: Point, c: Point, d: Point): boolean {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  if (o1 === 0 && o2 === 0 && o3 === 0 && o4 === 0) {
    return false;
  }
  return o1 !== o2 && o3 !== o4;
}

function orient(a: Point, b: Point, c: Point): number {
  const v = (b.y - a.y) * (c.x - a.x) - (b.x - a.x) * (c.y - a.y);
  if (Math.abs(v) < 1e-6) return 0;
  return v > 0 ? 1 : -1;
}

function clearSegment(a: Point, b: Point, obstacles: Rect[]): boolean {
  return !obstacles.some((r) => segmentHitsRect(a, b, r));
}

function polylineSvg(points: Point[]): string {
  if (points.length < 2) return "";
  const [first, ...rest] = points;
  return `M ${first.x},${first.y} ${rest.map((p) => `L ${p.x},${p.y}`).join(" ")}`;
}

function pathLength(points: Point[]): number {
  let s = 0;
  for (let i = 1; i < points.length; i++) s += dist(points[i - 1], points[i]);
  return s;
}

/** Visibility graph over start, end, and inflated obstacle corners; Dijkstra. */
export function computeAvoidingPath(
  start: Point,
  end: Point,
  obstacleNodes: Node[],
): { path: string; labelX: number; labelY: number } {
  const obstacles = obstacleNodes
    .map(nodeRect)
    .filter((r): r is Rect => Boolean(r))
    .map((r) => inflate(r, PAD));

  if (!obstacles.length || clearSegment(start, end, obstacles)) {
    const [path, labelX, labelY] = getStraightPath({
      sourceX: start.x,
      sourceY: start.y,
      targetX: end.x,
      targetY: end.y,
    });
    return { path, labelX, labelY };
  }

  const points: Point[] = [start, end];
  for (const r of obstacles) {
    for (const c of corners(r)) {
      if (!obstacles.some((o) => pointInRect(c, o))) {
        points.push(c);
      }
    }
  }

  const n = points.length;
  const adj: { to: number; w: number }[][] = Array.from({ length: n }, () => []);
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (clearSegment(points[i], points[j], obstacles)) {
        const w = dist(points[i], points[j]);
        adj[i].push({ to: j, w });
        adj[j].push({ to: i, w });
      }
    }
  }

  const distArr = Array(n).fill(Infinity);
  const prev = Array(n).fill(-1);
  const used = Array(n).fill(false);
  distArr[0] = 0;
  for (let iter = 0; iter < n; iter++) {
    let u = -1;
    let best = Infinity;
    for (let i = 0; i < n; i++) {
      if (!used[i] && distArr[i] < best) {
        best = distArr[i];
        u = i;
      }
    }
    if (u < 0 || best === Infinity) break;
    used[u] = true;
    if (u === 1) break;
    for (const { to, w } of adj[u]) {
      if (distArr[u] + w < distArr[to]) {
        distArr[to] = distArr[u] + w;
        prev[to] = u;
      }
    }
  }

  if (prev[1] < 0 && distArr[1] === Infinity) {
    const [path, labelX, labelY] = getStraightPath({
      sourceX: start.x,
      sourceY: start.y,
      targetX: end.x,
      targetY: end.y,
    });
    return { path, labelX, labelY };
  }

  const route: Point[] = [];
  for (let cur = 1; cur !== -1; cur = prev[cur]) {
    route.push(points[cur]);
    if (cur === 0) break;
  }
  route.reverse();
  if (route[0] !== start && (route[0].x !== start.x || route[0].y !== start.y)) {
    route.unshift(start);
  }

  const path = polylineSvg(route);
  const midLen = pathLength(route) / 2;
  let walked = 0;
  let labelX = (start.x + end.x) / 2;
  let labelY = (start.y + end.y) / 2;
  for (let i = 1; i < route.length; i++) {
    const seg = dist(route[i - 1], route[i]);
    if (walked + seg >= midLen && seg > 0) {
      const t = (midLen - walked) / seg;
      labelX = route[i - 1].x + (route[i].x - route[i - 1].x) * t;
      labelY = route[i - 1].y + (route[i].y - route[i - 1].y) * t;
      break;
    }
    walked += seg;
  }

  return { path, labelX, labelY };
}

export function MindmapEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style,
  markerEnd,
  label,
  selected,
}: EdgeProps) {
  const nodes = useNodes();

  const { path, labelX, labelY } = useMemo(() => {
    const obstacles = nodes.filter((n) => n.id !== source && n.id !== target && !n.hidden);
    return computeAvoidingPath({ x: sourceX, y: sourceY }, { x: targetX, y: targetY }, obstacles);
  }, [nodes, source, target, sourceX, sourceY, targetX, targetY]);

  const strokeWidth = useMemo(() => {
    const scaleOf = (id: string) => {
      const n = nodes.find((x) => x.id === id);
      const raw = (n?.data as { scale?: number } | undefined)?.scale;
      const s = Number.isFinite(raw) ? Math.round(raw as number) : 3;
      return Math.min(5, Math.max(1, s));
    };
    const s = Math.max(scaleOf(source), scaleOf(target));
    const base = 0.7 + s * 0.45;
    return selected ? base + 0.6 : base;
  }, [nodes, source, target, selected]);

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={{
          stroke: "#64748b",
          strokeWidth,
          ...style,
        }}
      />
      {label ? (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan pointer-events-none absolute rounded bg-white/90 px-1 text-[10px] text-slate-500"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}
